import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, Building2, User, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LazyImage } from '@/components/LazyImage';
import { useConnections, type ApiConnection, type ConnectionsQuery } from '@/lib/hooks/useConnections';
import { RELATIONSHIP_OPTIONS, ITEM_RELATIONSHIP_OPTIONS } from '@sizzlenz/shared';

/** Lookup map for relationship value → label */
const RELATIONSHIP_LABEL_MAP = new Map<string, string>([...RELATIONSHIP_OPTIONS, ...ITEM_RELATIONSHIP_OPTIONS].map(o => [o.value, o.label]));
interface ObjectConnectionsProps {
  /** Source object type */
  fromType?: string;
  /** Source object ID */
  fromId?: number;
  /** Target object type */
  toType?: string;
  /** Target object ID */
  toId?: number;
  /** Filter by relationship values (OR logic) */
  relationships?: string[];
  /** Items per page */
  limit?: number;
  /** Optional title shown above the list */
  title?: string;
  /** Optional CSS class */
  className?: string;
  /** Whether to show relationship badges on each row. Defaults to true. */
  showRelationships?: boolean;
  /** Group connections under relationship headings. Defaults to false. */
  groupByRelationship?: boolean;
}

/**
 * Compact, pageable list of connections for any object.
 * Shows the connected entity/item name, image, and relationship badges.
 */
export function ObjectConnections(t0) {
  const $ = _c(37);
  const {
    fromType,
    fromId,
    toType,
    toId,
    relationships,
    limit: t1,
    title,
    className,
    showRelationships: t2,
    groupByRelationship: t3
  } = t0;
  const limit = t1 === undefined ? 5 : t1;
  const showRelationships = t2 === undefined ? true : t2;
  const groupByRelationship = t3 === undefined ? false : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [""];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [cursors, setCursors] = useState(t4);
  const [currentPage, setCurrentPage] = useState(0);
  const t5 = cursors[currentPage] || undefined;
  let t6;
  if ($[1] !== fromId || $[2] !== fromType || $[3] !== limit || $[4] !== relationships || $[5] !== t5 || $[6] !== toId || $[7] !== toType) {
    t6 = {
      fromType,
      fromId,
      toType,
      toId,
      relationships,
      relationshipsOnly: true,
      limit,
      nextCursor: t5
    };
    $[1] = fromId;
    $[2] = fromType;
    $[3] = limit;
    $[4] = relationships;
    $[5] = t5;
    $[6] = toId;
    $[7] = toType;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const query = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      staleTime: 30000
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const {
    data,
    isLoading
  } = useConnections(query, t7);
  const page = data?.pages[0];
  let t8;
  if ($[10] !== page?.connections) {
    t8 = page?.connections ?? [];
    $[10] = page?.connections;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const connections = t8;
  const hasNext = !!page?.pagination.nextCursor;
  const hasPrev = currentPage > 0;
  let t9;
  bb0: {
    if (!groupByRelationship) {
      t9 = null;
      break bb0;
    }
    let map;
    if ($[12] !== connections) {
      map = new Map();
      for (const conn of connections) {
        const rels = conn.data?.relationships ?? [];
        if (rels.length === 0) {
          const bucket = map.get("_none") ?? [];
          bucket.push(conn);
          map.set("_none", bucket);
        } else {
          for (const rel of rels) {
            const bucket_0 = map.get(rel) ?? [];
            bucket_0.push(conn);
            map.set(rel, bucket_0);
          }
        }
      }
      $[12] = connections;
      $[13] = map;
    } else {
      map = $[13];
    }
    t9 = map;
  }
  const grouped = t9;
  if (!isLoading && connections.length === 0) {
    return null;
  }
  let t10;
  if ($[14] !== currentPage || $[15] !== page) {
    t10 = () => {
      if (page?.pagination.nextCursor) {
        setCursors(prev => [...prev.slice(0, currentPage + 1), page.pagination.nextCursor]);
        setCurrentPage(_temp);
      }
    };
    $[14] = currentPage;
    $[15] = page;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const handleNext = t10;
  let t11;
  if ($[17] !== currentPage) {
    t11 = () => {
      if (currentPage > 0) {
        setCurrentPage(_temp2);
      }
    };
    $[17] = currentPage;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handlePrev = t11;
  let t12;
  if ($[19] !== handleNext || $[20] !== handlePrev || $[21] !== hasNext || $[22] !== hasPrev) {
    t12 = (hasNext || hasPrev) && <div className="flex items-center justify-between mt-2"><Button variant="ghost" size="sm" onClick={handlePrev} disabled={!hasPrev} className="gap-1 text-xs h-7 px-2"><ChevronLeft className="h-3 w-3" />Prev</Button><Button variant="ghost" size="sm" onClick={handleNext} disabled={!hasNext} className="gap-1 text-xs h-7 px-2">Next<ChevronRight className="h-3 w-3" /></Button></div>;
    $[19] = handleNext;
    $[20] = handlePrev;
    $[21] = hasNext;
    $[22] = hasPrev;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const paginationControls = t12;
  let t13;
  if ($[24] !== title) {
    t13 = title && <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>;
    $[24] = title;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== connections || $[27] !== fromType || $[28] !== grouped || $[29] !== isLoading || $[30] !== paginationControls || $[31] !== showRelationships) {
    t14 = isLoading ? <div className="space-y-2">{[...Array(2)].map(_temp3)}</div> : grouped ? <><div className="space-y-4">{Array.from(grouped.entries()).map(t15 => {
          const [rel_0, conns] = t15;
          return <div key={rel_0}><h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{rel_0 === "_none" ? "Other" : RELATIONSHIP_LABEL_MAP.get(rel_0) ?? rel_0.replace(/_/g, " ")}</h4><ul className="divide-y divide-gray-100">{conns.map(conn_0 => <ConnectionRow key={conn_0.id} connection={conn_0} fromType={fromType} showRelationships={false} />)}</ul></div>;
        })}</div>{paginationControls}</> : <><ul className="divide-y divide-gray-100">{connections.map(conn_1 => <ConnectionRow key={conn_1.id} connection={conn_1} fromType={fromType} showRelationships={showRelationships} />)}</ul>{paginationControls}</>;
    $[26] = connections;
    $[27] = fromType;
    $[28] = grouped;
    $[29] = isLoading;
    $[30] = paginationControls;
    $[31] = showRelationships;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== className || $[34] !== t13 || $[35] !== t14) {
    t15 = <div className={className}>{t13}{t14}</div>;
    $[33] = className;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}

/** Compact row for a single connection */
function _temp3(_, i) {
  return <div key={i} className="flex items-center gap-3 animate-pulse"><div className="h-8 w-8 rounded-lg bg-gray-200" /><div className="flex-1 space-y-1"><div className="h-3 w-24 bg-gray-200 rounded" /><div className="h-2.5 w-16 bg-gray-200 rounded" /></div></div>;
}
function _temp2(p_0) {
  return p_0 - 1;
}
function _temp(p) {
  return p + 1;
}
function ConnectionRow(t0) {
  const $ = _c(22);
  const {
    connection,
    fromType,
    showRelationships
  } = t0;
  const showTo = !!fromType;
  const entity = showTo ? (connection as any).toEntity ?? (connection as any).entity : (connection as any).fromEntity;
  const item = showTo ? (connection as any).toItem : (connection as any).fromItem;
  const user = showTo ? (connection as any).toUser : (connection as any).fromUser;
  const objectType = showTo ? connection.toType : connection.fromType;
  const objectId = showTo ? connection.toId : connection.fromId;
  let t1;
  if ($[0] !== connection.data?.relationships) {
    t1 = connection.data?.relationships ?? [];
    $[0] = connection.data?.relationships;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const relationships = t1;
  let name;
  let image;
  let href;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Building2 className="h-4 w-4 text-gray-400" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let icon = t2;
  if (objectType === "entity" && entity) {
    name = entity.name_1;
    image = entity.image;
    href = `/entities/${objectId}`;
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Building2 className="h-4 w-4 text-gray-400" />;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    icon = t3;
  } else {
    if (objectType === "item" && item) {
      name = item.title;
      image = item.image;
      href = `/items/${objectId}`;
      let t3;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <Package className="h-4 w-4 text-gray-400" />;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      icon = t3;
    } else {
      if (objectType === "user" && user) {
        name = user.name || user.email;
        image = user.image;
        let t3;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <User className="h-4 w-4 text-gray-400" />;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        icon = t3;
      }
    }
  }
  if (!name) {
    name = `${objectType} ${objectId}`;
  }
  let t3;
  if ($[6] !== href || $[7] !== name) {
    t3 = href ? <Link href={href} className="text-sm font-medium text-gray-900 hover:text-primary-600 truncate cursor-pointer">{name}</Link> : <span className="text-sm font-medium text-gray-900 truncate">{name}</span>;
    $[6] = href;
    $[7] = name;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const nameEl = t3;
  let t4;
  if ($[9] !== icon || $[10] !== image || $[11] !== name) {
    t4 = image ? <LazyImage src={image} alt={name} className="h-8 w-8 rounded-lg object-cover shrink-0" /> : <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">{icon}</div>;
    $[9] = icon;
    $[10] = image;
    $[11] = name;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== relationships || $[14] !== showRelationships) {
    t5 = showRelationships && relationships.map(_temp4);
    $[13] = relationships;
    $[14] = showRelationships;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== nameEl || $[17] !== t5) {
    t6 = <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">{nameEl}{t5}</div>;
    $[16] = nameEl;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t6) {
    t7 = <li className="flex items-center gap-3 py-2">{t4}{t6}</li>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function _temp4(rel) {
  return <span key={rel} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700">{RELATIONSHIP_LABEL_MAP.get(rel) ?? rel.replace(/_/g, " ")}</span>;
}